import { CaseData } from '../types/case';
import { CaseListResponse, ListCaseRequest, CaseSummary as ApiCaseSummary, CaseSentListResponse, ListSentCasesRequest, RescindCaseRequest, RescindCaseResponse, CaseReceivedListResponse, ListReceivedCasesRequest, AcceptReceivedCaseRequest, AcceptReceivedCaseResponse, RejectReceivedCaseRequest, RejectReceivedCaseResponse, CopyReceivedCaseRequest, CopyReceivedCaseResponse, CopyCaseRequest, CopyCaseResponse, CopyCaseForSendingRequest, CopyCaseForSendingResponse, CopyCaseForReceivingRequest, CopyCaseForReceivingResponse, ListUsersForSendResponse, SendCaseRequest, SendCaseResponse, GetCaseSentRecipientsRequest, GetCaseSentRecipientsResponse, PendingServerChangesInfo, MergeSummary, CaseEditingStatus } from '../types/caseApi';
import { BaseApiService } from './BaseApiService';
import { sharedTokenRefresh } from './tokenRefresh';
import { debugLog, debugError } from '../utils/debug';
import { normalizeCaseData, hasAnyMissingFinancialLineId } from '../utils/caseDataNormalization';
import { lookupDataService, CountyLookup } from './lookupDataService';
import { getTabId } from '../utils/clientIdentifiers';
import type { ProDataReason } from '../utils/proDataReasons';

// Response wrapper to capture ETag headers
export interface ApiResponse<T> {
    data: T;
    etag?: string;
}

// New: response from the no-merge get/save routes — includes pendingServerChanges
// flag so the client can prompt the user to invoke /merge-and-save explicitly.
export interface NoMergeGetResponse {
    data: CaseData;
    etag?: string;
    pendingServerChanges: PendingServerChangesInfo | null;
    /**
     * True if normalization injected at least one brand-new lineId/groupLineId (a legacy case
     * saved before stable row identity existed). Signals the load path to persist those ids
     * immediately so lineId-keyed reconciliation is stable across loads/tabs from the first
     * open — see docs/AUTOSAVE_DETAILS.md section 13.
     */
    lineIdsBackfilled: boolean;
    /**
     * ProDowngrade: whether this case holds Pro-only data, from the X-Case-Contains-Pro-Data
     * response header. Undefined when the header is absent, meaning the backfill has not reached
     * this case yet -- which must be treated as unknown (never locked), not as false.
     * Combined with the team's effective subscription type, this is what makes a case read-only
     * for a team that has dropped off Pro.
     */
    containsProData?: boolean;
}

export interface NoMergeSaveResponse {
    etag?: string;
    createdAt?: string;
    updatedAt?: string;
    pendingServerChanges: PendingServerChangesInfo | null;
}

export interface MergeAndSaveResponse {
    data: CaseData;
    etag?: string;
    createdAt?: string;
    updatedAt?: string;
    mergeSummary: MergeSummary | null;
}

// Save case response structure from C# backend
export interface SaveCaseResponse {
    success: boolean;
    message: string;
    case: CaseData;
    createdAt?: string;
    updatedAt?: string;
    eTag?: string;
    expenseDataOverridden?: boolean;
    expenseData?: any;
}

// Error response interface
export interface ApiError {
    message: string;
    status: number;
    statusText: string;
}

export class CaseApiService extends BaseApiService {
    constructor() {
        super(sharedTokenRefresh);
    }

    /**
     * Get a case by ID
     * @param caseId - The case ID to retrieve
     * @param ensureChangesSaved - Whether to ensure all pending changes are saved before fetching (default: true)
     * @returns Promise containing the case data and ETag
     */
    async getCase(caseId: string, ensureChangesSaved: boolean = true): Promise<ApiResponse<CaseData>> {
        // Note: ensureChangesSaved parameter is kept for backward compatibility
        // but CaseStateManager should handle auto-save logic, not CaseApiService

        const response = await this.postJson('/v1/cases/get', { caseId });
        const data = await this.handleResponse<CaseData>(response);
        const etag = response.headers.get('ETag') || undefined;
        const expenseDataOverridden = response.headers.get('X-Expense-Data-Overridden') === 'true';

        // Normalize case data to handle old format migrations (e.g., snake_case to camelCase)
        // Get counties synchronously if already loaded (for countyName normalization)
        const counties = lookupDataService.getLookupDataByTableSync('countyLookup') as CountyLookup[] | null;
        const normalizedData = data ? normalizeCaseData(data, counties || undefined) : data;

        // If expense data was overridden, it's already integrated in the case data
        // Mark it so SimpleCaseContext can show the modal
        if (expenseDataOverridden && normalizedData) {
            (normalizedData as any)._expenseDataOverridden = true;
        }

        // Debug logging to see the payload structure
        // debugLog('🔍 getCase API Response:', {
        //     caseId,
        //     responseStatus: response.status,
        //     etag,
        //     data: normalizedData ?? null
        // });


        return { data: normalizedData, etag };
    }

    /**
     * Get a case by ID without ensuring changes are saved (useful for initial loads)
     * @param caseId - The case ID to retrieve
     * @returns Promise containing the case data and ETag
     */
    async getCaseWithoutSaveCheck(caseId: string): Promise<ApiResponse<CaseData>> {
        return this.getCase(caseId, false);
    }

    /**
     * Save/update a case
     * @param caseId - The case ID to save
     * @param caseData - The case data to save
     * @param etag - The ETag from the previous GET request (required for optimistic concurrency)
     * @returns Promise containing the updated case data and new ETag
     */
    async saveCase(
        caseId: string,
        caseData: CaseData,
        etag: string
    ): Promise<ApiResponse<CaseData>> {
        // debugLog('🔍 saveCase API Request:', {
        //     caseId,
        //     etag,
        //     hasCaseData: !!caseData
        // });

        // Validate team ID before saving
        // Note: Backend will use teamId from JWT token, but we should still validate
        // that the case data structure is reasonable
        if (caseData.team?.id === '') {
            console.warn('Case has empty string team.id, backend will use token teamId');
        }

        // Output caseData as JSON string to console before saving
        // debugLog('CaseData before save:', JSON.stringify(caseData, null, 2));

        // Pass CaseData as-is without any normalization or changes
        const response = await this.postJson('/v1/cases/save', caseData);
        const responseBody = await this.handleResponse<SaveCaseResponse & { expenseDataOverridden?: boolean; expenseData?: any }>(response);

        // Extract ETag from response body (preferred) or header (fallback)
        // C# property "ETag" is typically serialized as "eTag" in JSON
        const responseEtag = responseBody.eTag || (responseBody as any).ETag || response.headers.get('ETag') || undefined;

        // Extract case data from the 'Case' property
        // C# property "Case" is typically serialized as "case" in JSON (camelCase)
        const data = responseBody.case || (responseBody as any).Case;

        // Check for expense data override
        if (responseBody.expenseDataOverridden === true && responseBody.expenseData && data) {
            // Overwrite expense groups with client data
            if (!data.financial) {
                data.financial = {};
            }
            data.financial.expenseGroups = responseBody.expenseData;
            // Mark that expense data was overridden
            (data as any)._expenseDataOverridden = true;
        }

        // Normalize case data to handle old format migrations (e.g., snake_case to camelCase)
        // Get counties synchronously if already loaded (for countyName normalization)
        const counties = lookupDataService.getLookupDataByTableSync('countyLookup') as CountyLookup[] | null;
        const normalizedData = data ? normalizeCaseData(data, counties || undefined) : data;

        // Extract CreatedAt and UpdatedAt - handle both camelCase and PascalCase
        const createdAt = responseBody.createdAt || (responseBody as any).CreatedAt;
        const updatedAt = responseBody.updatedAt || (responseBody as any).UpdatedAt;

        // Update audit info with CreatedAt and UpdatedAt from response if provided
        if (normalizedData.audit) {
            if (createdAt) {
                normalizedData.audit.createdAt = createdAt;
            }
            if (updatedAt) {
                normalizedData.audit.updatedAt = updatedAt;
            }
        } else if (createdAt || updatedAt) {
            // Initialize audit if it doesn't exist but we have timestamps
            normalizedData.audit = {
                createdAt: createdAt,
                updatedAt: updatedAt
            };
        }

        // debugLog('🔍 saveCase API Response:', {
        //     caseId,
        //     success: responseBody.success,
        //     message: responseBody.message,
        //     hasCase: !!normalizedData,
        //     createdAt: createdAt,
        //     updatedAt: updatedAt,
        //     etag: responseEtag
        // });

        return { data: normalizedData, etag: responseEtag };
    }

    /**
     * Get a case without auto-merging client-submitted expenses.
     * The client is the master copy; if the server has pending submissions, they are
     * advertised via pendingServerChanges so the UI can prompt for an explicit merge.
     */
    async getCaseNoMerge(caseId: string): Promise<NoMergeGetResponse> {
        const response = await this.postJson('/v1/cases/get-no-merge', { caseId });
        const data = await this.handleResponse<CaseData>(response);
        const etag = response.headers.get('ETag') || undefined;

        const counties = lookupDataService.getLookupDataByTableSync('countyLookup') as CountyLookup[] | null;
        const lineIdsBackfilled = data ? hasAnyMissingFinancialLineId(data) : false;
        const normalizedData = data ? normalizeCaseData(data, counties || undefined) : data;

        const kind = response.headers.get('X-Pending-Server-Changes-Kind');
        const pendingServerChanges: PendingServerChangesInfo | null = kind
            ? {
                kind,
                linkTokenId: response.headers.get('X-Pending-Server-Changes-LinkTokenId') || undefined,
                submittedAt: response.headers.get('X-Pending-Server-Changes-SubmittedAt') || undefined,
            }
            : null;

        // Absent header => unknown, NOT false. A false verdict is an explicit "false".
        const containsProDataHeader = response.headers.get('X-Case-Contains-Pro-Data');
        const containsProData = containsProDataHeader === null ? undefined : containsProDataHeader === 'true';

        return { data: normalizedData, etag, pendingServerChanges, lineIdsBackfilled, containsProData };
    }

    /**
     * Save a case without auto-merging on the server. The client is the master copy.
     * Response intentionally does NOT include the case body — only the new etag and a
     * pendingServerChanges flag that signals when the client is "outdated" relative to
     * server-side data the user hasn't explicitly merged yet.
     */
    async saveCaseNoMerge(
        caseId: string,
        caseData: CaseData,
        etag: string
    ): Promise<NoMergeSaveResponse> {
        const response = await this.postJson('/v1/cases/save-no-merge', caseData, true, etag ? { 'If-Match': etag } : undefined);
        const responseBody = await this.handleResponse<{
            success: boolean;
            message: string;
            createdAt?: string;
            updatedAt?: string;
            eTag?: string;
            pendingServerChanges: PendingServerChangesInfo | null;
        } & { ETag?: string; CreatedAt?: string; UpdatedAt?: string }>(response);

        const responseEtag = responseBody.eTag || (responseBody as any).ETag || response.headers.get('ETag') || undefined;
        const createdAt = responseBody.createdAt || (responseBody as any).CreatedAt;
        const updatedAt = responseBody.updatedAt || (responseBody as any).UpdatedAt;

        return {
            etag: responseEtag,
            createdAt,
            updatedAt,
            pendingServerChanges: responseBody.pendingServerChanges ?? null,
        };
    }

    /**
     * Explicitly merge pending client-submitted expenses into the case and save.
     * Intended to be invoked from the UI behind a blocking modal — the response IS
     * applied to client state because the user explicitly opted in to the merge.
     */
    async mergeAndSaveCase(
        caseId: string,
        caseData: CaseData,
        etag: string
    ): Promise<MergeAndSaveResponse> {
        // Send If-Match so the merge is guarded against a concurrent save (backend returns 412 on
        // conflict). Without it, merge-and-save is an unconditional last-write-wins overwrite.
        const response = await this.postJson('/v1/cases/merge-and-save', caseData, true, etag ? { 'If-Match': etag } : undefined);
        const responseBody = await this.handleResponse<{
            success: boolean;
            message: string;
            case?: CaseData;
            createdAt?: string;
            updatedAt?: string;
            eTag?: string;
            mergeSummary: MergeSummary | null;
        } & { Case?: CaseData; ETag?: string; CreatedAt?: string; UpdatedAt?: string }>(response);

        const responseEtag = responseBody.eTag || (responseBody as any).ETag || response.headers.get('ETag') || undefined;
        const data = responseBody.case || (responseBody as any).Case;
        const createdAt = responseBody.createdAt || (responseBody as any).CreatedAt;
        const updatedAt = responseBody.updatedAt || (responseBody as any).UpdatedAt;

        const counties = lookupDataService.getLookupDataByTableSync('countyLookup') as CountyLookup[] | null;
        const normalizedData = data ? normalizeCaseData(data, counties || undefined) : data;

        if (normalizedData.audit) {
            if (createdAt) normalizedData.audit.createdAt = createdAt;
            if (updatedAt) normalizedData.audit.updatedAt = updatedAt;
        } else if (createdAt || updatedAt) {
            normalizedData.audit = { createdAt, updatedAt };
        }

        return {
            data: normalizedData,
            etag: responseEtag,
            createdAt,
            updatedAt,
            mergeSummary: responseBody.mergeSummary ?? null,
        };
    }

    /**
     * Get a case by ID for export (Root only). Case may belong to any team; no team restriction.
     * Returns case JSON; throws on 400 if not found.
     * @param caseId - Case ID (UUID string)
     * @returns Promise with case data
     */
    async getCaseById(caseId: string): Promise<CaseData> {
        const response = await this.postJson('/v1/cases/get-by-id', { caseId });
        const body = await this.handleResponse<SaveCaseResponse>(response);
        const data = body.case || (body as any).Case;
        if (!data) {
            throw new Error(body.message || 'Case not found');
        }
        const counties = lookupDataService.getLookupDataByTableSync('countyLookup') as CountyLookup[] | null;
        return normalizeCaseData(data, counties || undefined);
    }

    /**
     * Import a case from raw JSON (Root only). Backend assigns team/attorney and prepends "IMPORTED {date}: " to note.
     * @param caseData - Case JSON (e.g. from file)
     * @returns Promise with imported case data and etag
     */
    async importCase(caseData: CaseData): Promise<ApiResponse<CaseData>> {
        const response = await this.postJson('/v1/cases/import', caseData);
        const responseBody = await this.handleResponse<SaveCaseResponse>(response);
        const responseEtag = responseBody.eTag || (responseBody as any).ETag || response.headers.get('ETag') || undefined;
        const data = responseBody.case || (responseBody as any).Case;
        const counties = lookupDataService.getLookupDataByTableSync('countyLookup') as CountyLookup[] | null;
        const normalizedData = data ? normalizeCaseData(data, counties || undefined) : data;
        return { data: normalizedData, etag: responseEtag };
    }

    /**
     * Backup a case when save fails (sends raw request body without parsing)
     * @param caseId - The case ID to backup
     * @param rawRequestBody - The raw JSON string that was sent to save (before any parsing)
     * @param errorMessage - Optional error message from the failed save
     * @param errorType - Optional error type from the failed save
     * @returns Promise indicating success
     */
    async backupCase(
        caseId: string,
        rawRequestBody: string,
        errorMessage?: string,
        errorType?: string
    ): Promise<void> {
        try {
            // Just POST the raw body with Content-Type header
            const url = this.buildUrl('/v1/cases/backup');

            const response = await fetch(url, {
                method: 'POST',
                credentials: 'include',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: rawRequestBody, // Just the raw JSON string
            });

            if (!response.ok) {
                console.warn('Failed to backup case:', response.status);
            }
        } catch (error) {
            // Silently fail - backup failures shouldn't affect the user experience
            console.warn('Error backing up case:', error);
        }
    }

    /**
     * Create a new case
     * @param caseData - The case data to create
     * @returns Promise containing the created case data and ETag
     */
    async createCase(caseData: Omit<CaseData, 'basic'> & { basic: Omit<CaseData['basic'], 'id'> }): Promise<ApiResponse<CaseData>> {
        const response = await this.postJson('/v1/cases', caseData);
        const data = await response.json();
        const etag = response.headers.get('ETag') || undefined;
        return { data, etag };
    }


    /**
     * Get a list of cases for the current user
     * @param request - Optional request parameters
     * @returns Promise containing case list response
     */
    async getCases(request?: ListCaseRequest): Promise<ApiResponse<CaseListResponse>> {
        const response = await this.postJson('/v1/cases/list', request || {});
        const data = await this.handleResponse<CaseListResponse>(response);
        const etag = response.headers.get('ETag') || undefined;

        // Debug logging to see the payload structure
        // debugLog('🔍 getCases API Response:', {
        //     responseStatus: response.status,
        //     etag,
        //     data: data ? {
        //         casesCount: data.cases?.length || 0,
        //         firstCase: data.cases?.[0] ? {
        //             cid: data.cases[0].cid, // CaseId
        //             cno: data.cases[0].cno, // CaseNumber
        //             txyr: data.cases[0].txyr, // TaxYear
        //             afn: data.cases[0].afn, // PartyAFirstName
        //             aln: data.cases[0].aln, // PartyALastName
        //             bfn: data.cases[0].bfn, // PartyBFirstName
        //             bln: data.cases[0].bln, // PartyBLastName
        //             co: data.cases[0].co, // CountyName
        //             st: data.cases[0].st, // StateName
        //             n: data.cases[0].n, // Note
        //             crat: data.cases[0].crat, // CreatedAt
        //             upat: data.cases[0].upat, // UpdatedAt
        //             stat: data.cases[0].stat, // Status
        //             etag: data.cases[0].etag, // ETag
        //             fav: data.cases[0].fav, // IsFavorite
        //             pet: data.cases[0].pet, // PetitionerParty
        //             crby: data.cases[0].crby, // Created By (User ID GUID)
        //             upby: data.cases[0].upby, // Updated By (User ID GUID)
        //         } : null
        //     } : null
        // });

        return { data, etag };
    }

    /**
     * Copy a case from an existing case (uses the new CaseCopyService)
     * This endpoint verifies team ownership and should only be used for cases that belong to the user's team
     * @param caseId - The ID of the case to copy from
     * @returns Promise containing the response with the new case ID
     */
    async copyCase(caseId: string, newTaxYear?: number, custodyArrangement?: CopyCaseRequest['custodyArrangement'], requestId?: string): Promise<CopyCaseResponse> {
        try {
            const request: CopyCaseRequest = {
                caseId,
                requestId
            };
            if (newTaxYear !== undefined) {
                request.newTaxYear = newTaxYear;
            }
            if (custodyArrangement !== undefined) {
                request.custodyArrangement = custodyArrangement;
            }
            const response = await this.postJson('/v1/cases/copy', request);
            return await this.handleResponse<CopyCaseResponse>(response);
        } catch (error) {
            debugError('Error copying case:', error);
            throw error;
        }
    }

    /**
     * Copy a case for sending purposes (creates a new editable copy without team ownership verification)
     * This is used when preparing a case to send, and works with cases that may not belong to the user's team
     * @param caseId - The ID of the case to copy from
     * @param newTaxYear - Optional new tax year (must be >= 2025 if provided)
     * @returns Promise containing the response with the new case ID
     */
    async copyCaseForSending(caseId: string, newTaxYear?: number, requestId?: string): Promise<CopyCaseForSendingResponse> {
        try {
            const request: CopyCaseForSendingRequest = {
                caseId,
                requestId
            };
            if (newTaxYear !== undefined) {
                request.newTaxYear = newTaxYear;
            }
            const response = await this.postJson('/v1/cases/copy-for-sending', request);
            return await this.handleResponse<CopyCaseForSendingResponse>(response);
        } catch (error) {
            debugError('Error copying case for sending:', error);
            throw error;
        }
    }

    /**
     * Copy a case for receiving purposes (creates a new editable copy from a received case)
     * This is used when copying cases from the Received tab, and works with cases that don't belong to the user's team
     * @param caseId - The ID of the case to copy from
     * @param newTaxYear - Optional new tax year (must be >= 2025 if provided)
     * @returns Promise containing the response with the new case ID
     */
    async copyCaseForReceiving(caseId: string, newTaxYear?: number, requestId?: string): Promise<CopyCaseForReceivingResponse> {
        try {
            const request: CopyCaseForReceivingRequest = {
                caseId,
                requestId
            };
            if (newTaxYear !== undefined) {
                request.newTaxYear = newTaxYear;
            }
            const response = await this.postJson('/v1/cases/copy-for-receiving', request);
            return await this.handleResponse<CopyCaseForReceivingResponse>(response);
        } catch (error) {
            debugError('Error copying case for receiving:', error);
            throw error;
        }
    }

    async updateCaseStatus(caseId: string, newStatus: string): Promise<{ success: boolean; message: string; etag?: string; updatedAt?: string }> {
        try {
            const response = await this.postJson('/v1/cases/update-status', {
                caseId: caseId,
                newStatus: newStatus
            });
            return await this.handleResponse<{ success: boolean; message: string; etag?: string; updatedAt?: string }>(response);
        } catch (error) {
            debugError('Error updating case status:', error);
            throw error;
        }
    }

    async updateCaseNote(caseId: string, newNote: string): Promise<{ success: boolean; message: string; etag?: string; updatedAt?: string }> {
        try {
            // debugLog('updateCaseNote called with caseId:', caseId, 'newNote:', newNote);
            const response = await this.postJson('/v1/cases/update-note', {
                caseId: caseId,
                newNote: newNote
            });
            return await this.handleResponse<{ success: boolean; message: string; etag?: string; updatedAt?: string }>(response);
        } catch (error) {
            debugError('Error updating case note:', error);
            throw error;
        }
    }

    async toggleCaseFavorite(caseId: string, isFavorite: boolean): Promise<{ success: boolean; message: string }> {
        try {
            const response = await this.postJson('/v1/cases/toggle-favorite', {
                caseId: caseId,
                isFavorite: isFavorite
            });
            return await this.handleResponse<{ success: boolean; message: string }>(response);
        } catch (error) {
            debugError('Error toggling case favorite:', error);
            throw error;
        }
    }

    /**
     * Get a list of cases sent by the current user's team
     * @param request - Optional request parameters
     * @returns Promise containing sent cases list response
     */
    async getSentCases(request?: ListSentCasesRequest): Promise<ApiResponse<CaseSentListResponse>> {
        try {
            const response = await this.postJson('/v1/cases/list-sent', request || {});
            const data = await this.handleResponse<CaseSentListResponse>(response);
            return { data };
        } catch (error) {
            debugError('Error retrieving sent cases:', error);
            throw error;
        }
    }

    /**
     * Get a list of cases received by the current user's team
     * @param request - Optional request parameters
     * @returns Promise containing received cases list response
     */
    async getReceivedCases(request?: ListReceivedCasesRequest): Promise<ApiResponse<CaseReceivedListResponse>> {
        try {
            const response = await this.postJson('/v1/cases/list-received', request || {});
            const data = await this.handleResponse<CaseReceivedListResponse>(response);
            return { data };
        } catch (error) {
            debugError('Error retrieving received cases:', error);
            throw error;
        }
    }

    /**
     * Accept a received case
     * @param caseSentId - The case sent ID to accept
     * @param note - Optional note for the acceptance
     * @returns Promise containing the response
     */
    async acceptReceivedCase(
        caseSentId: string,
        note?: string,
        representedParty?: 'partyA' | 'partyB',
        attorneyId?: string,
        caseNote?: string,
        requestId?: string
    ): Promise<AcceptReceivedCaseResponse> {
        try {
            const response = await this.postJson('/v1/cases/accept-received', {
                caseSentId,
                note,
                representedParty,
                attorneyId,
                caseNote,
                requestId
            } as AcceptReceivedCaseRequest);
            return await this.handleResponse<AcceptReceivedCaseResponse>(response);
        } catch (error) {
            debugError('Error accepting received case:', error);
            throw error;
        }
    }

    /**
     * Reject a received case
     * @param caseSentId - The case sent ID to reject
     * @param rejectionNote - Optional note for the rejection
     * @returns Promise containing the response
     */
    async rejectReceivedCase(caseSentId: string, rejectionNote?: string): Promise<RejectReceivedCaseResponse> {
        try {
            const response = await this.postJson('/v1/cases/reject-received', {
                caseSentId,
                rejectionNote
            } as RejectReceivedCaseRequest);
            return await this.handleResponse<RejectReceivedCaseResponse>(response);
        } catch (error) {
            debugError('Error rejecting received case:', error);
            throw error;
        }
    }

    /**
     * Copy a received case (create a new copy from the original)
     * @param caseId - The case ID to copy
     * @returns Promise containing the response with the new case ID
     */
    async copyReceivedCase(caseId: string, requestId?: string): Promise<CopyReceivedCaseResponse> {
        try {
            const response = await this.postJson('/v1/cases/copy-received', {
                caseId,
                requestId
            } as CopyReceivedCaseRequest);
            return await this.handleResponse<CopyReceivedCaseResponse>(response);
        } catch (error) {
            debugError('Error copying received case:', error);
            throw error;
        }
    }

    /**
     * Rescind a sent case file
     * @param caseSentId - The case sent ID to rescind
     * @param cancelNote - Optional note for the rescission
     * @param caseReceivedIds - Optional list of specific case received IDs to rescind
     * @returns Promise containing the response
     */
    async rescindFile(caseSentId: string, cancelNote?: string, caseReceivedIds?: string[]): Promise<RescindCaseResponse> {
        try {
            const response = await this.postJson('/v1/cases/rescind-file', {
                caseSentId,
                cancelNote,
                caseReceivedIds
            } as RescindCaseRequest);
            return await this.handleResponse<RescindCaseResponse>(response);
        } catch (error) {
            debugError('Error rescinding case file:', error);
            throw error;
        }
    }

    /**
     * Get recipients for a sent case with their status
     * @param caseSentId - The case sent ID
     * @returns Promise containing the recipients list
     */
    async getCaseSentRecipients(caseSentId: string): Promise<GetCaseSentRecipientsResponse> {
        try {
            const response = await this.postJson('/v1/cases/sent/recipients', {
                caseSentId
            } as GetCaseSentRecipientsRequest);
            return await this.handleResponse<GetCaseSentRecipientsResponse>(response);
        } catch (error) {
            debugError('Error getting case sent recipients:', error);
            throw error;
        }
    }

    /**
     * List all active users for send case autocomplete
     * @returns Promise containing list of active users
     */
    async listUsersForSend(): Promise<ListUsersForSendResponse> {
        try {
            const response = await this.postJson('/v1/cases/users/list-for-send', {});
            return await this.handleResponse<ListUsersForSendResponse>(response);
        } catch (error) {
            debugError('Error listing users for send:', error);
            throw error;
        }
    }

    /**
     * Send case file to recipients
     * @param caseId - The case ID to send
     * @param recipientUserIds - Array of user IDs to send to
     * @param messageNote - Optional message note
     * @returns Promise containing the response
     */
    async sendCase(caseId: string, recipientUserIds: string[], messageNote?: string): Promise<SendCaseResponse> {
        try {
            const response = await this.postJson('/v1/cases/send-case', {
                caseId,
                recipientUserIds,
                messageNote
            } as SendCaseRequest);
            return await this.handleResponse<SendCaseResponse>(response);
        } catch (error) {
            debugError('Error sending case:', error);
            throw error;
        }
    }

    /**
     * Marks a case as currently being edited by the calling user (ConcurrentCaseEditGuard).
     * No-op (silently ignored) when the feature flag is off -- the endpoint 404s and callers
     * are expected to swallow the failure rather than surface it to the user.
     * @param caseId - The case ID being opened for editing
     */
    async markEditing(caseId: string): Promise<void> {
        try {
            await this.postJson(`/v1/cases/${caseId}/mark-editing`, { tabId: getTabId() });
        } catch (error) {
            debugError('Error marking case as being edited:', error);
        }
    }

    /**
     * Records a client-detected concurrency event (takeover, silent same-user force-save, merge
     * retry, ...) into the server logs so it's filterable on the admin logs page
     * (concurrency_event column). Fire-and-forget -- never disrupts the user. `event` should be a
     * short stable token (see ConcurrencyEvents on the backend).
     */
    async logConcurrencyEvent(caseId: string, event: string, detail?: string): Promise<void> {
        try {
            await this.postJson(`/v1/cases/${caseId}/concurrency-log`, { event, detail });
        } catch (error) {
            debugError('Error logging concurrency event:', error);
        }
    }

    /**
     * Refreshes this tab's editing lease AND returns the case's current (TTL-filtered) editing
     * marker in one round-trip (ConcurrentCaseEditGuard). Called by the wizard's presence poll:
     * the conditional server heartbeat keeps a live editor's marker from expiring, and the
     * returned marker (incl. `edbytab` and `clientEditing`) lets the caller detect when a
     * *different* tab took over and whether a client is active in the expense portal. Returns the
     * full status object (edby may be absent while clientEditing is true), or undefined on any
     * error / flag-off 404 (fail-open).
     * @param caseId - The case being edited
     */
    async heartbeatEditing(caseId: string): Promise<CaseEditingStatus | undefined> {
        try {
            const response = await this.postJson(`/v1/cases/${caseId}/heartbeat-editing`, { tabId: getTabId() });
            return await this.handleResponse<CaseEditingStatus>(response);
        } catch (error) {
            debugError('Error heartbeating case editing marker:', error);
            return undefined;
        }
    }

    /**
     * Clears the "currently editing" marker for a case, if still held by this tab. Called on
     * wizard exit (in-app navigation, logout). For browser/tab close, use clearEditingBeacon
     * instead -- a normal fetch here would be aborted mid-flight.
     * @param caseId - The case ID being left
     */
    async clearEditing(caseId: string): Promise<void> {
        try {
            await this.postJson(`/v1/cases/${caseId}/clear-editing`, { tabId: getTabId() });
        } catch (error) {
            debugError('Error clearing case editing marker:', error);
        }
    }

    /**
     * Freshly reads a case's "currently editing" marker, bypassing whatever the client's cached
     * case list already has (ConcurrentCaseEditGuard). Used right before navigating into the
     * wizard from the homepage Edit button, since the list snapshot can go stale between when it
     * was fetched and when the user clicks. Returns the full status object (edby may be absent
     * while `clientEditing` is true -- a client editing the portal with no wizard editor), or
     * undefined (fail-open) on any error / flag-off 404. Callers check the specific field they need.
     * @param caseId - The case ID about to be opened for editing
     */
    /**
     * ProDowngrade: the Pro-only data this case holds, in plain language -- why it is read-only
     * for a non-Pro team, and what a copy will not include. Same walker as the server's lock
     * predicate, so the list matches the lock. Returns null on any error; callers fall back to
     * generic wording.
     */
    async getProDataReasons(caseId: string): Promise<ProDataReason[] | null> {
        try {
            const response = await this.postJson(`/v1/cases/${caseId}/pro-data-reasons`, {});
            const result = await this.handleResponse<{ success?: boolean; reasons?: ProDataReason[] }>(response);
            return result?.success && Array.isArray(result.reasons) ? result.reasons : null;
        } catch (error) {
            debugError('Error reading Pro data reasons:', error);
            return null;
        }
    }

    async getEditingStatus(caseId: string): Promise<CaseEditingStatus | undefined> {
        try {
            const response = await this.postJson(`/v1/cases/${caseId}/editing-status`, {});
            return await this.handleResponse<CaseEditingStatus>(response);
        } catch (error) {
            debugError('Error checking case editing status:', error);
            return undefined;
        }
    }

    /**
     * Clears the "currently editing" marker via a keepalive/sendBeacon request that survives
     * page teardown -- for `pagehide` (browser/tab close), mirroring
     * BackgroundSaveService.flushBeacon's transport choice. The tab id rides in the BODY (not a
     * header) because sendBeacon cannot set custom headers, and the clear is tab-scoped server-side.
     * @param caseId - The case ID being left
     */
    clearEditingBeacon(caseId: string): void {
        const url = this.buildUrl(`/v1/cases/${caseId}/clear-editing`);
        const body = JSON.stringify({ tabId: getTabId() });
        try {
            // keepalive fetch is the preferred approach: it supports credentials and fires
            // even after the page starts unloading (same rationale as flushBeacon).
            fetch(url, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body,
                credentials: 'include',
                keepalive: true,
            }).catch(() => { /* best-effort -- can't handle errors after unload */ });
        } catch {
            // Fallback: sendBeacon, relying on the HTTP-only cookie for auth.
            try {
                navigator.sendBeacon?.(url, new Blob([body], { type: 'application/json' }));
            } catch { /* truly best-effort */ }
        }
    }

}

// Case summary interface is now defined in types/caseApi.ts

// Singleton instance
export const caseApiService = new CaseApiService();
